// ---------------------------------------------------------------
// TruckMate dispatcher feed — reads the data the on-premise connector
// delivers into the backend (/truckmate/ingest), plus the delivered-trip
// queue the dispatcher/AI acts on.
//
// The connector posts to POST /truckmate/ingest with a shared key. The
// dispatcher UI reads it back through these authenticated endpoints:
//   GET  /truckmate/ingest/latest?site=…   → latest snapshot (trips + meta)
//   GET  /truckmate/delivered?site=…        → delivered-trip queue
//   POST /truckmate/delivered/ack           → mark one handled
//
// Auth is the same Bearer/JWT the fleet + admin app already use.
// ---------------------------------------------------------------
import { API_BASE } from './config';
import { getJwt, sessionEnded } from './traccar';

export const TM_SITE = 'florida-beauty';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  ...(getJwt() ? { Authorization: `Bearer ${getJwt()}` } : {}),
});

const asJson = async (r) => {
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    if (r.status === 401 && /session expired|not signed in/i.test(String(body.error || ''))) sessionEnded();
    // keep the server's flags (needPhone, needConsent, …) on the error
    const e = new Error(body.error || `Request failed (${r.status})`);
    Object.assign(e, body, { status: r.status });
    throw e;
  }
  return body;
};

// Latest snapshot the connector delivered: { site, receivedAt, collectedAt,
// tripCount, ageMinutes, paths, errors, data: { trips, rosters, baseline } }.
export async function getTruckMateStatus(site = TM_SITE) {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/truckmate/ingest/latest?site=${encodeURIComponent(site)}`, {
    credentials: 'include', headers: authHeaders(),
  });
  return asJson(r);
}

// The whole active board (every live trip, not just the latest delta), plus a
// heartbeat: { site, count, trips, receivedAt, ageMinutes }. This is what the
// dispatcher panel reads so the trip count is stable instead of flickering with
// each connector cycle.
export async function getActiveTrips(site = TM_SITE) {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/truckmate/active?site=${encodeURIComponent(site)}`, {
    credentials: 'include', headers: authHeaders(),
  });
  return asJson(r);
}

// GPS breadcrumb for one truck (power unit) → { points:[{t,lat,lng,mph}], vehicle,
// from, to, count }. `since` (ISO) bounds it to the trip start; else last `hours`.
export async function fetchTripRoute(unit, { hours, since } = {}) {
  if (!API_BASE) throw new Error('No backend configured.');
  const qs = new URLSearchParams();
  if (hours) qs.set('hours', String(hours));
  if (since) qs.set('since', since);
  const r = await fetch(`${API_BASE}/truckmate/route/${encodeURIComponent(unit)}${qs.toString() ? `?${qs}` : ''}`, {
    credentials: 'include', headers: authHeaders(),
  });
  return asJson(r);
}

// ---- Rate confirmation: upload a PDF/image, AI reads out the details ----
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => { const s = String(fr.result || ''); resolve(s.slice(s.indexOf(',') + 1)); };
    fr.onerror = () => reject(new Error('Could not read the file.'));
    fr.readAsDataURL(file);
  });
}

// Upload one OR several files/pages of a rate con. Some brokers send a multi-page
// rate con as separate photos/PDFs, so this accepts a single File or a list and
// sends every page to the AI in one read so it sees the whole document.
export async function uploadRateCon(trip, fileOrFiles, site = TM_SITE) {
  if (!API_BASE) throw new Error('No backend configured.');
  const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
  if (!files.length) throw new Error('No file selected.');
  const total = files.reduce((n, f) => n + (f.size || 0), 0);
  if (total > 24 * 1024 * 1024) throw new Error('Files are too large (max ~24 MB total).');
  const pages = await Promise.all(files.map(async (f) => ({
    dataBase64: await fileToBase64(f), mediaType: f.type || '', filename: f.name || '',
  })));
  const r = await fetch(`${API_BASE}/truckmate/ratecon/${encodeURIComponent(trip)}?site=${encodeURIComponent(site)}`, {
    method: 'POST', credentials: 'include', headers: authHeaders(),
    // keep single-file fields for back-compat; `pages` carries all of them
    body: JSON.stringify({ pages, dataBase64: pages[0].dataBase64, mediaType: pages[0].mediaType, filename: pages[0].filename }),
  });
  return asJson(r);
}

export async function getRateCon(trip, site = TM_SITE) {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/truckmate/ratecon/${encodeURIComponent(trip)}?site=${encodeURIComponent(site)}`, {
    credentials: 'include', headers: authHeaders(),
  });
  return asJson(r);
}

// Check / uncheck one handling instruction. The backend stamps the current
// dispatcher's name + time as proof and returns the trip's full sign-off map.
export async function setRcInstruction(trip, instruction, done, site = TM_SITE) {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/truckmate/ratecon/${encodeURIComponent(trip)}/check?site=${encodeURIComponent(site)}`, {
    method: 'POST', credentials: 'include', headers: authHeaders(),
    body: JSON.stringify({ instruction, done: !!done }),
  });
  return asJson(r);
}

// Delivered-trip queue (newest first). unacked=true → only ones not yet handled.
export async function getDelivered(site = TM_SITE, unacked = false) {
  if (!API_BASE) throw new Error('No backend configured.');
  const q = `site=${encodeURIComponent(site)}${unacked ? '&unacked=1' : ''}`;
  const r = await fetch(`${API_BASE}/truckmate/delivered?${q}`, {
    credentials: 'include', headers: authHeaders(),
  });
  return asJson(r);
}

// Mark a delivered trip handled so it drops out of the unacked list.
export async function ackDelivered(tripNumber, site = TM_SITE) {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/truckmate/delivered/ack`, {
    method: 'POST', credentials: 'include', headers: authHeaders(),
    body: JSON.stringify({ site, tripNumber: String(tripNumber) }),
  });
  return asJson(r);
}

// ---- shaping the raw TruckMate rows into something the UI can render ----

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const round1 = (n) => Math.round(n * 10) / 10;

// TruckMate status codes → friendly label + a colour bucket. We fall back to
// the row's own statusDesc text (and a neutral colour) for anything unmapped,
// so a code we've never seen still shows sensibly.
const STATUS_MAP = [
  [/^(avail|avbl|new)/i, { label: 'Available', tone: 'idle' }],
  [/^(disp|assigned)/i, { label: 'Dispatched', tone: 'go' }],
  [/^arrship/i, { label: 'At shipper', tone: 'wait' }],
  [/^(depship|intran|enroute)/i, { label: 'In transit', tone: 'go' }],
  [/^arrcons/i, { label: 'At consignee', tone: 'wait' }],
  [/^(delvd|deliv|del$|cmplt|complete)/i, { label: 'Delivered', tone: 'done' }],
  [/^spot/i, { label: 'Spotted', tone: 'wait' }],
  [/^(canc|void)/i, { label: 'Cancelled', tone: 'bad' }],
  [/^hold/i, { label: 'On hold', tone: 'bad' }],
];

export function statusMeta(code, desc) {
  const c = String(code || '');
  for (const [re, meta] of STATUS_MAP) if (re.test(c)) return meta;
  const label = (desc && String(desc).trim())
    ? String(desc).replace(/\b\w/g, (m) => m.toUpperCase())
    : (c || 'Unknown');
  return { label, tone: 'idle' };
}

// Flatten one ingest trip item ({ _id, trip:{…}, freightBills:[…] }) into a
// flat record the board renders. Defensive about shape — older payloads put the
// trip fields at the top level and call the bills "orders".
export function normalizeTrip(item) {
  const t = (item && item.trip) || item || {};
  const billsRaw = (item && (item.freightBills || item.orders)) || t.freightBills || [];
  const bills = (Array.isArray(billsRaw) ? billsRaw : []).map((b) => ({
    billNumber: String(b.billNumber ?? b.billNo ?? b.bill ?? ''), // exact string — keeps prefixes and leading zeros
    billTo: b.billToName || b.billTo || '',
    commodity: b.commodity || '',
    pieces: num(b.pieces),        // boxes (piecesUnits is BOX for floral)
    cube: num(b.cube),            // cubic feet (CBF) — TruckMate "Cbs"
    pallets: num(b.pallets),
    temp: num(b.temperature),     // REQUIRED setpoint (not a live reefer reading)
    tempUnits: b.temperatureUnits || 'F',
    weight: num(b.weight),
    charges: num(b.totalCharges || b.charges),
    from: b.startZoneDescription || b.startZone || '',
    to: b.endZoneDescription || b.endZone || '',
    service: b.serviceLevel || '',
    deliverBy: b.deliverBy || '',
    deliverByEnd: b.deliverByEnd || '',
    pickUpBy: b.pickUpBy || '',
    pickUpByEnd: b.pickUpByEnd || '',
    eta: b.estimatedDeliveryDate || b.deliverBy || '',   // system's estimated delivery
    miles: num(b.distance),                              // loaded miles for this bill/leg
    actualPickup: b.actualPickup || '',
    opCode: b.opCode || '',                              // operation / lane code
    siteId: b.siteId || '',                              // origin terminal/site
    createdTime: b.createdTime || '',
    createdBy: b.createdBy || '',
    pickedUp: !!(b.actualPickup) || b.pickupDone === 'True' || b.pickupDone === true,
    delivered: !!(b.actualDelivery),
    deliveredAt: b.actualDelivery || '',                 // TruckMate delivery time (wall clock, as entered)
    status: b.status || '',
    statusDesc: b.statusDescription || b.longStatusDescription || '',
  }));
  const current = t.currentZoneDesc || t.currentZone || '';
  const temps = bills.map((b) => b.temp).filter((tp) => tp > 0);
  const reqTempMin = temps.length ? Math.min(...temps) : null;
  const reqTempMax = temps.length ? Math.max(...temps) : null;
  const tempUnits = (bills.find((b) => b.tempUnits) || {}).tempUnits || 'F';

  // Live Samsara overlay + the discrepancies between it and TruckMate.
  const live = (item && item._samsara) || null;
  const alerts = [];
  if (live) {
    const nm = (s) => String(s || '').trim().toLowerCase();
    // reefer temp more than 3° off the setpoint (or the required temp) — only on
    // a fresh reading (stale legacy reefers report months-old values)
    if (live.tempF != null && !live.tempStale) {
      const target = live.setpointF != null ? live.setpointF : reqTempMin;
      if (target != null && Math.abs(live.tempF - target) > 3) {
        alerts.push({ kind: 'temp', msg: `Reefer ${live.tempF}°${tempUnits}, target ${target}°${tempUnits} (${live.tempF > target ? '+' : ''}${round1(live.tempF - target)}°)` });
      }
    }
    // driver on the truck (per Samsara) isn't EITHER of the trip's drivers.
    // Teams run two drivers and Samsara shows whichever is active/assigned, so we
    // only flag when the truck's driver matches neither Driver 1 nor Driver 2.
    if (live.samsaraDriver && (live.driver1 || live.driver2)) {
      const s = nm(live.samsaraDriver);
      const d1 = nm(live.driver1); const d2 = nm(live.driver2);
      if (!((d1 && s === d1) || (d2 && s === d2))) {
        alerts.push({ kind: 'driver', msg: `Truck shows ${live.samsaraDriver}, trip has ${live.driver1 || '—'}${live.driver2 ? ` / ${live.driver2}` : ''}` });
      }
    }
    // NOTE: no location-mismatch alert. Samsara streams GPS continuously while
    // TruckMate only updates its zone on a status/geofence event, so a moving
    // truck legitimately shows two different spots — that's timing lag, not a
    // problem. Both locations are still shown in the panel for eyeballing.
  }

  return {
    id: String((item && item._id) || t.tripNumber || ''),
    tripNumber: t.tripNumber || (item && item._id) || '',
    statusCode: t.status || '',
    statusDesc: t.statusDesc || t.statusDescription || '',
    driver: t.driver || '',
    driver2: t.driver2 || '',
    trailer: t.trailer || t.trailer2 || '',
    powerUnit: t.powerUnit || '',
    origin: t.origZoneDesc || '',
    destination: t.destZoneDesc || '',
    current,
    eta: t.eTA || t.expectedDate || '',
    etd: t.eTD || '',
    legCount: num(t.legCount),
    activeLeg: num(t.activeLeg),
    originZone: t.originZone || '',
    opCode: (bills.find((b) => b.opCode) || {}).opCode || '',
    siteId: (bills.find((b) => b.siteId) || {}).siteId || '',
    createdTime: (bills.find((b) => b.createdTime) || {}).createdTime || '',
    createdBy: (bills.find((b) => b.createdBy) || {}).createdBy || '',
    delivered: item && item._event === 'delivered',
    rateCon: (item && item._ratecon) || null,   // AI-read rate confirmation (if uploaded)
    rcCheck: (item && item._rccheck) || {},      // handling-instruction sign-offs (by/at, keyed by text)
    commitments: (item && item._commit) || {},  // 📅 date + time committed to the customer, per stop ("CITY|ST")
    manifest: (item && item._manifest) || null,  // uploaded paper trip sheet (+ diffs vs TruckMate)
    // ARTIS customers on the run (client IDs → Trip Manifest PDFs) and ARTIS's own ETA per customer (read twice a day)
    artisStops: item && item._tb && Array.isArray(item._tb.stops) ? item._tb.stops.filter((s) => s.consigneeId).map((s) => ({ clientId: String(s.consigneeId), name: s.consignee || '', city: s.city || '', state: s.state || '' })) : [],
    artisEta: (item && item._artisEta) || null,
    artisLoc: (item && item._artisLoc) || null,         // ARTIS's own truck / trailer position (second GPS source)
    artisDoorTemp: (item && item._artisDoorTemp) || null, // trailer loaded far off its set temp (ARTIS door check)
    artisRun: item && item._tb ? { hub: item._tb.hub || null, date: item._tb.date || null, route: item._tb.route || null } : null,   // on ARTIS Truck Builder (warehouse + ship date)
    visits: (item && item._visits) || {},        // server-tracked geofence visits, keyed by trip-sheet stop key
    docs: (item && item._docs) || [],            // stored ORIGINAL documents (metadata; bytes via fetchStoredDoc)
    oc: (item && item._oc) || null,              // outside carrier (OC) — carrier, their truck/driver, source
    checkins: (item && item._checkins) || [],    // carrier check-ins: check calls, emails (newest first)
    packet: (item && item._packet) || [],        // every page of uploaded trip packets matched to this trip
    driverLink: (item && item._driverLink) || null, // OC driver tracking link (TagAlong app) — status, last fix
    breakdown: (item && item._breakdown) || null,
    waiting: (item && item._waiting) || null,        // waiting time clock at a stop
    breakdownOpen: !!(item && item._bdOpen),         // a breakdown reported and not closed yet
    tasks: (item && item._tasks) || [],
    hold: (item && item._hold) || null,
    notes: (item && item._notes) || [],               // updates / tasks / transfers recorded through Jarvis chat
    ocChat: (item && item._ocChat) || null,           // { count, unread, last } — chat with the OC driver's app
    nextLoad: (item && item._nextLoad) || [],
    loadSheet: (item && item._loadSheet) || null,
    stopFlow: (item && item._stopFlow) || null,     // an LTL load the driver works stop by stop in the app (stopflow.js)   // the load message dispatch sent the driver (saved, and whether they said OK)
    tmNext: (item && item._tmNext) || [],
    queuedBehind: (item && item._queuedBehind) || null,   // a next load waiting behind the truck's current run { trip, truck, pickupEtaMs, … }
    artis: (item && item._artis) || null,            // ARTIS boxes scanned vs expected for this load's customers            // trips TruckMate already has assigned to this truck after this run
    plan: (item && item._plan) || null,               // this truck's line on Gus's planning sheet { region, kind, next, notes }         // rate cons dispatch sent for this truck's NEXT run (verified when TruckMate has it)
    pickupFollow: (item && item._pickupFollow) || null,   // { plannedAt, steps:['ack','t60'…], outcome:{status,eta,source}, emailed }             // { kind: 'pickup_delayed'|…, note, newPickupAt, since, from } — read from a dispatcher's email
    finishHint: (item && item._finishHint) || null,   // "Probably finished — truck 2401 moved on to trip 624407"               // to-dos Jarvis found in emails about this load     // { on, at, by, note } — dispatcher turned on Breakdown
    emails: (item && item._emails) || null,          // { count, open, last } from the Jarvis inbox
    comms: (item && item._comms) || [],           // calls / texts to the driver and their replies (newest first)
    stopConfirm: (item && item._stopConfirm) || {}, // stops the driver confirmed delivered by text
    times: (item && item._times) || null,         // created (TruckMate) / added (to the board) / dispatched
    driverConsent: (item && item._driverConsent) || {}, // last-10 phone → texts consent (who/when), per driver
    bills,
    billCount: bills.length,
    boxes: bills.reduce((s, b) => s + b.pieces, 0),
    cube: bills.reduce((s, b) => s + b.cube, 0),
    pallets: bills.reduce((s, b) => s + b.pallets, 0),
    weight: bills.reduce((s, b) => s + b.weight, 0),
    charges: bills.reduce((s, b) => s + b.charges, 0),
    // trip miles ≈ the farthest stop (no trip-level distance field); rev/mi from it
    miles: bills.reduce((mx, b) => Math.max(mx, b.miles || 0), 0),
    reqTempMin, reqTempMax, tempUnits,
    live, alerts,
  };
}

// ---- Watchtower: the server's always-on Priority 1 monitor ----
//   GET  /watchtower/board     → open + recently resolved alerts
//   POST /watchtower/ack       → "I'm on it" (stamps name + time)
//   POST /watchtower/resolve   → close with a note
//   GET/PUT /watchtower/config → who gets Priority 1 pushes (TagAlong app)
//   POST /watchtower/test      → test push to everyone on the list
//   GET  /watchtower/handoff   → shift handoff summary
const wtGet = async (path) => {
  if (!API_BASE) throw new Error('No backend configured.');
  return asJson(await fetch(`${API_BASE}${path}`, { credentials: 'include', headers: authHeaders() }));
};
const wtSend = async (method, path, body) => {
  if (!API_BASE) throw new Error('No backend configured.');
  return asJson(await fetch(`${API_BASE}${path}`, { method, credentials: 'include', headers: authHeaders(), body: JSON.stringify({ site: TM_SITE, ...body }) }));
};
export const getWatchBoard = (site = TM_SITE) => wtGet(`/watchtower/board?site=${encodeURIComponent(site)}`);
export const ackWatchAlert = (id) => wtSend('POST', '/watchtower/ack', { id });
export const resolveWatchAlert = (id, note) => wtSend('POST', '/watchtower/resolve', { id, note });
export const getWatchConfig = () => wtGet('/watchtower/config');
export const setWatchConfig = (cfg) => wtSend('PUT', '/watchtower/config', cfg);
export const testWatchPush = () => wtSend('POST', '/watchtower/test', {});
export const getWatchHandoff = (site = TM_SITE) => wtGet(`/watchtower/handoff?site=${encodeURIComponent(site)}`);

// ---- Outbound trip sheets (paper manifests) read by the AI ----
//   POST /truckmate/manifests   { pages:[…] } → { trips:[…] }  (one batch per day)
//   GET  /truckmate/manifests                 → stored sheets, newest first
// Phone photos are shrunk to ~2000px JPEG first: plenty for handwriting, and
// keeps a 20-page batch under the server's upload limit.
async function shrinkImage(file, maxDim = 2000, quality = 0.85) {
  if (!/^image\//.test(file.type || '')) return { dataBase64: await fileToBase64(file), mediaType: file.type || 'application/pdf', filename: file.name || '' };
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bmp.width * scale); canvas.height = Math.round(bmp.height * scale);
  canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((ok) => canvas.toBlob(ok, 'image/jpeg', quality));
  return { dataBase64: await fileToBase64(blob), mediaType: 'image/jpeg', filename: file.name || '' };
}
// onStage(stage) reports progress: 'storing' (originals) → 'extracting' (AI read).
export async function uploadManifests(files, site = TM_SITE, onStage = null) {
  if (!API_BASE) throw new Error('No backend configured.');
  if (!files.length) throw new Error('No pages selected.');
  // 1) keep every ORIGINAL file exactly as uploaded (one request per file, so
  //    full-size photos fit), all tagged with this batch id
  const batchId = `b${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
  if (onStage) onStage('storing');
  const originalIds = [];
  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    const r = await fetch(`${API_BASE}/truckmate/docs?site=${encodeURIComponent(site)}`, { // eslint-disable-line no-await-in-loop
      method: 'POST', credentials: 'include', headers: authHeaders(),
      body: JSON.stringify({ kind: 'tripsheet', batchId, files: [{ dataBase64: await fileToBase64(f), mediaType: f.type || '', filename: f.name || '', page: i + 1 }] }), // eslint-disable-line no-await-in-loop
    });
    const j = await asJson(r); // eslint-disable-line no-await-in-loop
    // a multi-page PDF comes back split into one stored page each
    const ids = (j.docs || []).map((d) => d.id);
    originalIds.push(ids.length > 1 ? ids : (ids[0] || null));
  }
  // 2) send lighter copies to the AI reader, with the originals' ids
  if (onStage) onStage('extracting');
  const pages = [];
  for (const f of files) pages.push(await shrinkImage(f)); // eslint-disable-line no-await-in-loop
  const bytes = pages.reduce((n, p) => n + p.dataBase64.length, 0);
  if (bytes > 14 * 1024 * 1024) throw new Error('These pages are too large together — upload them in two batches.');
  const r = await fetch(`${API_BASE}/truckmate/manifests?site=${encodeURIComponent(site)}`, {
    method: 'POST', credentials: 'include', headers: authHeaders(), body: JSON.stringify({ pages, originalIds, batchId }),
  });
  return asJson(r);
}

// ---- stored original documents (trip sheets / rate cons) ----
export async function listTripDocs(trip, site = TM_SITE) {
  if (!API_BASE) throw new Error('No backend configured.');
  return asJson(await fetch(`${API_BASE}/truckmate/docs?site=${encodeURIComponent(site)}&trip=${encodeURIComponent(trip)}`, { credentials: 'include', headers: authHeaders() }));
}
// Fetch an original with the normal login (Bearer) — never a public link —
// and hand back a private in-memory URL for the in-page viewer. The caller
// revokes it when the viewer closes.
export async function fetchStoredDoc(id, { reveal = false, site = TM_SITE } = {}) {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/truckmate/docs/${encodeURIComponent(id)}/file?site=${encodeURIComponent(site)}${reveal ? '&reveal=1' : ''}`, { credentials: 'include', headers: authHeaders() });
  if (r.status === 403) {
    const j = await r.json().catch(() => ({}));
    if (j.restricted) { const e = new Error(j.docType === 'driver_id' ? 'This page is a driver ID — restricted.' : 'This file is restricted (it contains a driver ID).'); e.restricted = true; throw e; }
  }
  if (r.status === 401 || r.status === 403) throw new Error('Your session has expired — sign in again to open the document.');
  if (r.status === 404) throw new Error('This original file is no longer stored.');
  if (!r.ok) throw new Error(`Could not open the document (${r.status}).`);
  const blob = await r.blob();
  if (!blob.size) throw new Error('The stored file is empty.');
  return { url: URL.createObjectURL(blob), type: blob.type || r.headers.get('Content-Type') || '' };
}
// Several stored pages as one PDF (a trip's manifest: page 1 + its continuation).
export async function fetchMergedDocs(ids, { name = 'trip-sheet', site = TM_SITE } = {}) {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/truckmate/docs/merged?site=${encodeURIComponent(site)}&ids=${ids.map(encodeURIComponent).join(',')}&name=${encodeURIComponent(name)}`, { credentials: 'include', headers: authHeaders() });
  if (r.status === 401 || r.status === 403) throw new Error('Your session has expired — sign in again to open the document.');
  if (r.status === 404) throw new Error('The trip sheet pages are no longer stored.');
  if (!r.ok) throw new Error(`Could not open the trip sheet (${r.status}).`);
  const blob = await r.blob();
  return { url: URL.createObjectURL(blob), type: blob.type || 'application/pdf' };
}
// Load rundown: the full PDF report of a load (live preview or the finished one).
export async function fetchRundownPdf(trip, { site = TM_SITE } = {}) {
  if (!API_BASE) throw new Error('No backend configured.');
  const r = await fetch(`${API_BASE}/truckmate/rundown/${encodeURIComponent(trip)}/pdf?site=${encodeURIComponent(site)}`, { credentials: 'include', headers: authHeaders() });
  if (r.status === 401 || r.status === 403) throw new Error('Your session has expired — sign in again.');
  if (r.status === 404) throw new Error('No record of that load.');
  if (!r.ok) throw new Error(`Could not build the rundown (${r.status}).`);
  const blob = await r.blob();
  return { url: URL.createObjectURL(blob), type: 'application/pdf' };
}
export const sendRundown = (trip, to) => tmJson('POST', `/truckmate/rundown/${encodeURIComponent(trip)}/send`, to ? { to } : {});
export const getRundowns = () => tmJson('GET', '/truckmate/rundowns');
// Morning outbound report ("OUTBOUND N TRIP SHEETS - TUE 10/06/26"): last night's loads out of the Miami yard.
export const getOutbound = (date, refresh = false) => tmJson('GET', `/truckmate/outbound?${date ? `date=${encodeURIComponent(date)}&` : ''}${refresh ? 'refresh=1' : ''}`);
export const saveOutboundNote = (date, note) => tmJson('POST', '/truckmate/outbound/note', { date, note });
export const sendOutbound = (date) => tmJson('POST', '/truckmate/outbound/send', { date });
export const saveOutboundSettings = (body) => tmJson('PUT', '/truckmate/outbound/settings', body);
// Flower loads update for the owners / customer service.
export const getFlowerReport = () => tmJson('GET', '/truckmate/flower-report');
export const getStopTimes = (days = 7) => tmJson('GET', `/truckmate/stop-times?days=${days}`);
export const sendFlowerReport = () => tmJson('POST', '/truckmate/flower-report/send', {});
export const saveFlowerReportSettings = (body) => tmJson('PUT', '/truckmate/flower-report/settings', body);
export const getRundownSettings = () => tmJson('GET', '/truckmate/rundown/settings');
export const saveRundownSettings = (body) => tmJson('PUT', '/truckmate/rundown/settings', body);
export const testRundownEmail = () => tmJson('POST', '/truckmate/rundown/test-email', {});
export async function getManifests(site = TM_SITE) {
  if (!API_BASE) throw new Error('No backend configured.');
  return asJson(await fetch(`${API_BASE}/truckmate/manifests?site=${encodeURIComponent(site)}`, { credentials: 'include', headers: authHeaders() }));
}

// ---- outside carriers (OC) ----
const tmJson = async (method, path, body) => {
  if (!API_BASE) throw new Error('No backend configured.');
  return asJson(await fetch(`${API_BASE}${path}${path.includes('?') ? '&' : '?'}site=${encodeURIComponent(TM_SITE)}`, {
    method, credentials: 'include', headers: authHeaders(), ...(body ? { body: JSON.stringify(body) } : {}),
  }));
};
export const getCarriers = () => tmJson('GET', '/truckmate/carriers');
export const saveCarrier = (c) => (c.id ? tmJson('PUT', `/truckmate/carriers/${encodeURIComponent(c.id)}`, c) : tmJson('POST', '/truckmate/carriers', c));
export const markOutsideCarrier = (trip, body) => tmJson('POST', `/truckmate/oc/${encodeURIComponent(trip)}`, body);
export const logCheckin = (trip, body) => tmJson('POST', `/truckmate/checkins/${encodeURIComponent(trip)}`, body);
export const getUnmatchedPages = () => tmJson('GET', '/truckmate/packet/unmatched');
export const finishTrip = (trip, reason) => tmJson('POST', `/truckmate/trips/${encodeURIComponent(trip)}/finish`, { reason });
export const reopenTrip = (trip) => tmJson('POST', `/truckmate/trips/${encodeURIComponent(trip)}/reopen`, {});
// Chat with the OC driver in the TagAlong app (push notification to the driver's phone).
export const getOcMessages = (trip) => tmJson('GET', `/truckmate/oc/${encodeURIComponent(trip)}/messages`);
export const sendOcMessage = (trip, text) => tmJson('POST', `/truckmate/oc/${encodeURIComponent(trip)}/messages`, { text });
// "Ask Jarvis" — the dispatchers' chat with the AI dispatcher.
export const jarvisChat = (body) => tmJson('POST', '/jarvis/chat', body);
export const getJarvisThreads = () => tmJson('GET', '/jarvis/threads');
export const getJarvisVoice = () => tmJson('GET', '/jarvis/voice');
export const transcribeSpeech = (audio, mimeType, lang) => tmJson('POST', '/jarvis/transcribe', { audio, mimeType, lang });
// 📞 Call Jarvis: a one-time token for a live voice call in the browser (same Jarvis as the phone line).
export const startJarvisWebCall = () => tmJson('POST', '/voice/web-call', {});
export const getJarvisThread = (id) => tmJson('GET', `/jarvis/threads/${encodeURIComponent(id)}`);
export const decideJarvisAction = (id, decision) => tmJson('POST', `/jarvis/actions/${encodeURIComponent(id)}/${decision}`, {});
// Dispatcher accounts (admins only)
// AI-dispatcher customers (companies) — platform admin only
export const listCompanies = () => tmJson('GET', '/platform/companies');
export const createCompany = (body) => tmJson('POST', '/platform/companies', body);
export const listDispatchers = () => tmJson('GET', '/admin/dispatchers');
// a breakdown reported from a load's card. Photos shrink here when the browser can read them; iPhone HEIC goes as it is and the server converts it
const breakdownPhotos = async (files) => {
  const photos = await Promise.all(files.map((f) => shrinkImage(f, 1600, 0.8).catch(async () => ({ dataBase64: await fileToBase64(f), mediaType: f.type || 'image/heic', filename: f.name || '' }))));
  return photos;
};
// no limit on how many: they go up in batches the server takes (about 10 MB each); the first with the report, the rest onto its email chain
const photoBatches = (photos, room = 10 * 1024 * 1024) => photos.reduce((out, p) => { const last = out[out.length - 1]; if (last.length && last.reduce((n, x) => n + x.dataBase64.length, 0) + p.dataBase64.length > room) out.push([p]); else last.push(p); return out; }, [[]]);
const morePhotos = async (trip, rest) => { for (const batch of rest) await tmJson('POST', '/truckmate/breakdown/photos', { trip, photos: batch }); }; // eslint-disable-line no-await-in-loop
// a new breakdown from pictures (the driver's texts + the damage): Jarvis reads the words and finds the load; dispatch checks and sends
export const readBreakdownPictures = async (files, { note = '', truck = '', trailer = '', driver = '' } = {}) => tmJson('POST', '/truckmate/breakdown/read', { images: (await breakdownPhotos(files)).filter((p) => /^image\/(jpeg|png|webp|gif)$/i.test(p.mediaType || '')), note, truck, trailer, driver });
export const reportBreakdown = async (trip, { part, problem, driverSays, truck, trailer, files = [] }) => {
  const [first, ...rest] = photoBatches(await breakdownPhotos(files));
  const r = await tmJson('POST', '/truckmate/breakdown/report', { trip, part, problem, driverSays, truck, trailer, photos: first });
  await morePhotos(trip, rest);
  return r;
};
export const getOpenBreakdown = (trip) => tmJson('GET', `/truckmate/breakdown/open/${encodeURIComponent(trip)}`);
export const updateBreakdown = async (trip, { message, files = [] }) => {
  const [first, ...rest] = photoBatches(await breakdownPhotos(files));
  const r = await tmJson('POST', '/truckmate/breakdown/update', { trip, message, photos: first });
  await morePhotos(trip, rest);
  return r;
};
export const getAudit = ({ actor = '', trip = '', before = null } = {}) => tmJson('GET', `/admin/audit?${new URLSearchParams({ ...(actor ? { actor } : {}), ...(trip ? { trip } : {}), ...(before ? { before: String(before) } : {}) })}`);
export const getRequests = () => tmJson('GET', '/truckmate/requests');
export const sendRequestMessage = async (id, text, files = []) => {
  const list = await Promise.all(files.map((f) => shrinkImage(f, 1600, 0.8).catch(async () => ({ dataBase64: await fileToBase64(f), mediaType: f.type || 'application/octet-stream', filename: f.name || '' }))));
  if (list.reduce((n, p) => n + p.dataBase64.length, 0) > 13 * 1024 * 1024) throw new Error('These files are too big to send together. Remove one or two.');
  return tmJson('POST', id ? `/truckmate/requests/${encodeURIComponent(id)}/message` : '/truckmate/requests', { text, files: list });
};
export const finishRequest = (id) => tmJson('POST', `/truckmate/requests/${encodeURIComponent(id)}/done`, {});
// the load message for the driver (FB#, TRIP#, PICK UP, DELIVERY): read it, then save + send it
export const previewLoadSheet = (text, trip) => tmJson('POST', '/truckmate/loadsheet/preview', { text, trip });
export const sendLoadSheet = (text, trip, via, release) => tmJson('POST', '/truckmate/loadsheet', { text, trip, via, ...(release !== undefined ? { release } : {}) });
export const getLoadSheet = (trip) => tmJson('GET', `/truckmate/loadsheet/${encodeURIComponent(trip)}`);
// which stops go to a yard after a free / paid release (null clears it), and the yards dispatch has used
export const setLoadRelease = (trip, release) => tmJson('PUT', `/truckmate/loadsheet/${encodeURIComponent(trip)}/release`, release || { release: null });
export const getYards = () => tmJson('GET', '/truckmate/yards');
// the rate con made into the driver's load (TruckMate's stops and times): a draft, sent only when dispatch says so
export const getRateConDraft = (trip) => tmJson('GET', `/truckmate/loadsheet/${encodeURIComponent(trip)}/ratecon-draft`);
export const sendRateConLoad = (trip, via, release) => tmJson('POST', `/truckmate/loadsheet/${encodeURIComponent(trip)}/ratecon-send`, { via, ...(release !== undefined ? { release } : {}) });
export const getNextAsk = () => tmJson('GET', '/truckmate/asks/next');
export const answerAsk = (id, body) => tmJson('POST', `/truckmate/asks/${encodeURIComponent(id)}`, body);
export const addDispatcher = (body) => tmJson('POST', '/admin/dispatchers', body);
export const updateDispatcher = (id, body) => tmJson('PUT', `/admin/dispatchers/${encodeURIComponent(id)}`, body);
export const resetDispatcher = (id, how) => tmJson('POST', `/admin/dispatchers/${encodeURIComponent(id)}/reset`, { how });
export const removeDispatcher = (id) => tmJson('DELETE', `/admin/dispatchers/${encodeURIComponent(id)}`);
// Callback requests: someone (call / email / text / driver app) needs us to reach out.
export const getHelpRequests = (all = false) => tmJson('GET', `/truckmate/help${all ? '?all=1' : ''}`);
export const markHelpHandled = (id, note) => tmJson('POST', `/truckmate/help/${encodeURIComponent(id)}/handled`, { note });
export const getHelpSettings = () => tmJson('GET', '/truckmate/help/settings');
export const getCallerNumbers = (q) => tmJson('GET', `/truckmate/caller-numbers${q ? `?q=${encodeURIComponent(q)}` : ''}`);
export const setCallerNumber = (phone, name) => tmJson('POST', `/truckmate/caller-numbers/${encodeURIComponent(String(phone).replace(/\D+/g, ''))}`, { name });
export const removeCallerNumber = (phone) => tmJson('POST', `/truckmate/caller-numbers/${encodeURIComponent(String(phone).replace(/\D+/g, ''))}/remove`, {});
export const getHelpTask = (id) => tmJson('GET', `/truckmate/help/task/${encodeURIComponent(id)}`);
export const updateHelpTask = (id, body) => tmJson('POST', `/truckmate/help/${encodeURIComponent(id)}/update`, body);
export const createTaskFromCall = (body) => tmJson('POST', '/truckmate/help/from-call', body);
export const getHelpAssignees = () => tmJson('GET', '/truckmate/help/assignees');
export const saveHelpSettings = (body) => tmJson('PUT', '/truckmate/help/settings', body);
// Missing boxes cases (Missing boxes tab): customers who reported boxes missing — what they said + what the system shows
export const getMissingCases = () => tmJson('GET', '/truckmate/missing-cases');
export const updateMissingCase = (id, body) => tmJson('POST', `/truckmate/missing-cases/${encodeURIComponent(id)}`, body);
// Road calls (breakdowns / tires → Michelin ONCall): list, open, ask Jarvis to check the driver, update, approve
export const getRoadCalls = () => tmJson('GET', '/truckmate/roadcalls');
export const openRoadCall = (body) => tmJson('POST', '/truckmate/roadcalls', body);
export const checkDriverRoadCall = (trip, note) => tmJson('POST', '/truckmate/roadcalls/check', { trip, note });
export const updateRoadCall = (id, body) => tmJson('POST', `/truckmate/roadcalls/${encodeURIComponent(id)}`, body);
export const approveRoadCall = (id) => tmJson('POST', `/truckmate/roadcalls/${encodeURIComponent(id)}/approve`, {});
// Customer & broker profiles (Customers & brokers tab)
export const listProfiles = (type, q) => tmJson('GET', `/truckmate/profiles?${type ? `type=${encodeURIComponent(type)}&` : ''}${q ? `q=${encodeURIComponent(q)}` : ''}`);
// Geofences (customer locations from ARTIS) — the AI dispatcher's stop arrival / departure tracking
export const getGeofenceMap = () => tmJson('GET', '/truckmate/geofences/map');
export const getGeofences = (q = '') => tmJson('GET', `/truckmate/geofences${q ? `?q=${encodeURIComponent(q)}` : ''}`);
export const saveGeofence = (id, body) => tmJson('PUT', `/truckmate/geofences/${encodeURIComponent(id)}`, body);
export const syncArtisClients = () => tmJson('POST', '/truckmate/artis/clients/sync', {});
export const getProfile = (id) => tmJson('GET', `/truckmate/profiles/${encodeURIComponent(id)}`);
export const saveProfile = (id, body) => tmJson('PUT', `/truckmate/profiles/${encodeURIComponent(id)}`, body);
export const addProfile = (body) => tmJson('POST', '/truckmate/profiles', body);
export const mergeProfile = (id, into) => tmJson('POST', `/truckmate/profiles/${encodeURIComponent(id)}/merge`, { into });
export const deleteProfile = (id) => tmJson('DELETE', `/truckmate/profiles/${encodeURIComponent(id)}`);
export const syncProfiles = () => tmJson('POST', '/truckmate/profiles/sync', {});
export const importProfiles = (csv) => tmJson('POST', '/truckmate/profiles/import', { csv });
export const getProfileSettings = () => tmJson('GET', '/truckmate/profiles/settings');
export const saveProfileSettings = (body) => tmJson('PUT', '/truckmate/profiles/settings', body);
export async function downloadProfilesCsv(type) {
  const r = await fetch(`${API_BASE}/truckmate/profiles/export.csv?site=${encodeURIComponent(TM_SITE)}${type ? `&type=${type}` : ''}`, { credentials: 'include', headers: authHeaders() });
  if (!r.ok) throw new Error(`Could not download (${r.status}).`);
  return r.blob();
}
// Calls & texts log (by day) and Jarvis call transcript emails
export const getActivity = (date, type, q) => tmJson('GET', `/truckmate/activity?date=${encodeURIComponent(date)}${type && type !== 'all' ? `&type=${type}` : ''}${q ? `&q=${encodeURIComponent(q)}` : ''}`);
export const getActivityDays = () => tmJson('GET', '/truckmate/activity/days');
export const getMilestoneSettings = () => tmJson('GET', '/truckmate/milestones/settings');
export const saveMilestoneSettings = (body) => tmJson('PUT', '/truckmate/milestones/settings', body);
export const getProfileActivity = (id) => tmJson('GET', `/truckmate/activity/for-profile/${encodeURIComponent(id)}`);
export const getActivityCall = (callId, at) => tmJson('GET', `/truckmate/activity/call/${encodeURIComponent(callId)}${at ? `?at=${encodeURIComponent(at)}` : ''}`);
// admins: who gets which push notifications
// training mode (admins switch it; everyone sees the banner)
// scheduled ETA updates ("ETA every 3 hours on Native")
// load follow-through (rate-con instructions, Jarvis' offers, revised rate cons)
// what staff taught Jarvis (training emails)
// company directory (dispatchers: name + extension; admins: also the system-only cell / email)
export const getDirectory = () => tmJson('GET', '/truckmate/directory');
export const saveDirectory = (body) => tmJson('PUT', '/admin/directory', body);
export const addEmployee = (person) => tmJson('POST', '/admin/directory/people', person);
export const editEmployee = (id, person) => tmJson('PUT', `/admin/directory/people/${encodeURIComponent(id)}`, person);
export const getDriverPins = () => tmJson('GET', '/admin/driver-pins');
// every load's Driver app section: drivers signed in, notifications, and the load sent / notified / opened / confirmed
export const getDriverApp = () => tmJson('GET', '/truckmate/driver-app');
// Driver profiles: every driver with their ID, cells, truck, trailer and load; dispatch makes or edits a profile
export const getDriverProfiles = () => tmJson('GET', '/truckmate/driver-profiles');
export const saveDriverProfile = (code, body) => tmJson('PUT', `/truckmate/driver-profiles/${encodeURIComponent(code)}`, body);
export const removeDriverProfile = (code) => tmJson('DELETE', `/truckmate/driver-profiles/${encodeURIComponent(code)}`);
export const driverPin = (id, what) => tmJson('POST', `/admin/driver-pins/${encodeURIComponent(id)}/${what}`);
export const eraseEmployee = (id) => tmJson('POST', `/admin/directory/people/${encodeURIComponent(id)}/erase`);
export const saveDepartments = (departments) => tmJson('PUT', '/admin/directory/departments', { departments });
export const getPlanSheet = () => tmJson('GET', '/truckmate/plan-sheet');
export const savePlanSheet = (body) => tmJson('PUT', '/truckmate/plan-sheet/settings', body);
export const refreshPlanSheet = () => tmJson('POST', '/truckmate/plan-sheet/refresh', {});
export const sendPlanSheet = () => tmJson('POST', '/truckmate/plan-sheet/send', {});
export const saveEmailGroups = (groups) => tmJson('PUT', '/admin/directory/groups', { groups });
export const getAlertRecipients = () => tmJson('GET', '/truckmate/alert-recipients');
export const saveAlertRecipients = (alerts, texts) => tmJson('PUT', '/truckmate/alert-recipients', { alerts, ...(texts ? { texts } : {}) });
// 📅 the date + time committed to the customer for one stop (local: "YYYY-MM-DDTHH:MM" in the stop's time zone, or null to clear)
// ARTIS Trip Manifest PDF for one customer on a trip (kept on the trip) → { id, name }
// ARTIS (Synergize) POD / BOL / invoice for these bills — checked now, kept on the trip → { documents: [{ type, bill, docId, name, note }] }
export const checkArtisDocs = (trip, bills) => tmJson('POST', `/truckmate/artis/documents/${encodeURIComponent(trip)}`, { bills });
// clientId, or (when the stop isn't linked to ARTIS) { customer, city, state } — the server finds the ARTIS customer
export const getArtisManifest = (trip, clientId, kind = 'detail', stop = null) => tmJson('POST', `/truckmate/artis/manifest/${encodeURIComponent(trip)}`, { ...(clientId ? { clientId } : {}), kind, ...(stop || {}) });
export const setStopCommitment = (trip, body) => tmJson('PUT', `/truckmate/commitments/${encodeURIComponent(trip)}`, body);
export const getStaffContacts = (id) => tmJson('GET', `/truckmate/directory/${encodeURIComponent(id)}/contacts`);
// an employee's Recents: Jarvis + RingCentral calls (in / out / missed)
export const getStaffCalls = (id) => tmJson('GET', `/truckmate/directory/${encodeURIComponent(id)}/calls`);
export const contactEmployee = (id, body) => tmJson('POST', `/truckmate/directory/${encodeURIComponent(id)}/contact`, body);
export const getPlaybook = () => tmJson('GET', '/truckmate/playbook');
export const updateLesson = (id, body) => tmJson('PUT', `/truckmate/playbook/${encodeURIComponent(id)}`, body);
export const teachJarvis = (body) => tmJson('POST', '/truckmate/playbook/teach', body);
export const deleteLesson = (id) => tmJson('DELETE', `/truckmate/playbook/${encodeURIComponent(id)}`);
export const getFollow = () => tmJson('GET', '/truckmate/follow');
export const saveFollowSettings = (body) => tmJson('PUT', '/truckmate/follow/settings', body);
export const setCloseout = (trip, status) => tmJson('POST', `/truckmate/follow/${encodeURIComponent(trip)}/closeout`, { status });
export const getEtaWatches = () => tmJson('GET', '/truckmate/eta-watch');
export const stopEtaWatch = (id) => tmJson('DELETE', `/truckmate/eta-watch/${encodeURIComponent(id)}`);
export const getTraining = () => tmJson('GET', '/truckmate/training');
export const saveTraining = (body) => tmJson('PUT', '/admin/training', body);
export const getPushRules = () => tmJson('GET', '/admin/push-rules');
export const savePushRules = (people) => tmJson('PUT', '/admin/push-rules', { people });
export const testPushRule = (email) => tmJson('POST', '/admin/push-rules/test', { email });
export const getTranscriptSettings = () => tmJson('GET', '/voice/transcripts/settings');
export const saveTranscriptSettings = (body) => tmJson('PUT', '/voice/transcripts/settings', body);
export const clearHold = (trip) => tmJson('POST', `/truckmate/hold/${encodeURIComponent(trip)}/clear`, {});
// Jarvis follows each pickup with the driver (got-it text, 1 h and 30 min check-ins) and emails dispatch what the driver said.
export const getPickupFollowSettings = () => tmJson('GET', '/truckmate/pickup-follow/settings');
export const savePickupFollowSettings = (body) => tmJson('PUT', '/truckmate/pickup-follow/settings', body);
// Loads whose truck / trailer moved on while TruckMate still has them open → "Not closed" tab + emails to dispatch.
export const getUnclosedSettings = () => tmJson('GET', '/truckmate/unclosed/settings');
export const saveUnclosedSettings = (body) => tmJson('PUT', '/truckmate/unclosed/settings', body);
export const setTaskDone = (trip, id, done) => tmJson('POST', `/truckmate/tasks/${encodeURIComponent(trip)}/${encodeURIComponent(id)}`, { done });
export const setTaskOwner = (trip, id) => tmJson('POST', `/truckmate/tasks/${encodeURIComponent(trip)}/${encodeURIComponent(id)}`, { own: true });
export const getPendingRateCons = () => tmJson('GET', '/truckmate/ratecons/pending');
export const assignRateCon = (id, trip) => tmJson('POST', '/truckmate/ratecons/assign', { id, trip });
export const assignPacketPage = (body) => tmJson('POST', '/truckmate/packet/assign', body);
// OC driver tracking link — create (replaces the old one), text it, mark it sent, turn it off.
export const createDriverLink = (trip) => tmJson('POST', `/truckmate/oc/${encodeURIComponent(trip)}/link`, {});
export const textDriverLink = (trip, to) => tmJson('POST', `/truckmate/oc/${encodeURIComponent(trip)}/link/send`, to ? { to } : {});
export const markDriverLinkSent = (trip, via) => tmJson('POST', `/truckmate/oc/${encodeURIComponent(trip)}/link/sent`, { via });
export const revokeDriverLink = (trip) => tmJson('POST', `/truckmate/oc/${encodeURIComponent(trip)}/link/revoke`, {});
// RingCentral texting (company-wide). Keys go straight to our server and are never sent back.
export const getRingCentral = () => tmJson('GET', '/ringcentral/config');
export const connectRingCentral = (body) => tmJson('PUT', '/ringcentral/config', body);
export const getRingCentralNumbers = () => tmJson('GET', '/ringcentral/numbers');
export const setRingCentralFrom = (fromNumber) => tmJson('PUT', '/ringcentral/from-number', { fromNumber });
export const sendTestText = (to, text) => tmJson('POST', '/ringcentral/sms', { to, text });
// Calls (RingOut: your phone rings first) and texts to the driver of a load.
export const getMyPhone = () => tmJson('GET', '/ringcentral/my-phone');
export const setMyPhone = (phone) => tmJson('PUT', '/ringcentral/my-phone', { phone });
export const callPhone = (to, { trip, label } = {}) => tmJson('POST', '/ringcentral/call', { to, trip: trip || null, label: label || null });
// the browser phone (RingCentral Embeddable) and the dispatchers' recorded calls
export const getPhoneSetup = () => tmJson('GET', '/ringcentral/embeddable');
export const getCallTranscriptStatus = () => tmJson('GET', '/truckmate/call-transcripts/status');
export const noteCallIntent = (to, trip) => tmJson('POST', '/truckmate/call-intent', { to, trip: trip || null });
// the console's own phone: this dispatcher's RingCentral line (connected once), and who a number is for the call bar
export const getPhoneSip = () => tmJson('GET', '/ringcentral/phone/sip');
export const connectPhone = (body) => tmJson('POST', '/ringcentral/phone/connect', body);
export const disconnectPhone = () => tmJson('POST', '/ringcentral/phone/disconnect', {});
export const phoneWho = (n) => tmJson('GET', `/truckmate/phone/who?n=${encodeURIComponent(n)}`);
// the Phone tab: recent calls (RingCentral's log + Jarvis's calls) with summary, recording and transcript flags
export const getPhoneHistory = (days = 7) => tmJson('GET', `/truckmate/phone/history?days=${encodeURIComponent(days)}`);
// managers: calls going on right now, and listening in on one
export const getLiveCalls = () => tmJson('GET', '/truckmate/phone/live');
export const shadowLive = (session) => tmJson('POST', '/ringcentral/phone/shadow', { session });
// main-line (call queue) calls on this line: ringing or not; direct calls to the line ring either way
export const getQueueCalls = () => tmJson('GET', '/ringcentral/phone/queue');
export const setQueueCalls = (on) => tmJson('POST', '/ringcentral/phone/queue', { on });
// Phone & Messages: the shared company inbox (texts on the company line + driver-app messages)
export const getInbox = () => tmJson('GET', '/truckmate/messages');
export const getConversation = (id) => tmJson('GET', `/truckmate/messages/thread/${encodeURIComponent(id)}`);
export const markConversationUnread = (id) => tmJson('POST', `/truckmate/messages/thread/${encodeURIComponent(id)}/unread`, {});
export const sendInboxMessage = (body) => tmJson('POST', '/truckmate/messages/send', body);
export const createMessageGroup = (body) => tmJson('POST', '/truckmate/messages/groups', body);
export const saveMessagePins = (pins) => tmJson('PUT', '/truckmate/messages/pins', { pins });
export const searchMessageContacts = (q = '') => tmJson('GET', `/truckmate/messages/contacts?q=${encodeURIComponent(q)}`);
export const setSmsConsent = (trip, agreed) => tmJson('POST', `/truckmate/oc/${encodeURIComponent(trip)}/consent`, { agreed });
export const textDriver = (trip, body) => tmJson('POST', `/truckmate/trips/${encodeURIComponent(trip)}/text`, body);
export const setDriverConsent = (phone, name, agreed) => tmJson('POST', '/truckmate/consent', { phone, name, agreed });
// Everything said with one driver (calls, texts, replies), newest first, each tagged with its load.
// ---- Jarvis inbox (emails to the company mailbox, filed on loads) ----
export const getEmailStatus = () => tmJson('GET', '/truckmate/emails/status');
export const getEmails = ({ trip, unmatched } = {}) => tmJson('GET', `/truckmate/emails${trip ? `?trip=${encodeURIComponent(trip)}` : unmatched ? '?unmatched=1' : ''}`);
export const checkEmails = () => tmJson('POST', '/truckmate/emails/check', {});
export const assignEmail = (id, trip) => tmJson('POST', `/truckmate/emails/${encodeURIComponent(id)}/assign`, { trip });
export const rerunEmail = (id) => tmJson('POST', `/truckmate/emails/${encodeURIComponent(id)}/rerun`, {});
export const emailHandled = (id) => tmJson('POST', `/truckmate/emails/${encodeURIComponent(id)}/handled`, {});
export const draftEmailReply = (id) => tmJson('POST', `/truckmate/emails/${encodeURIComponent(id)}/draft`, {});
export const replyEmail = (id, text, docIds = []) => tmJson('POST', `/truckmate/emails/${encodeURIComponent(id)}/reply`, { text, docIds });
export const getInboxSettings = () => tmJson('GET', '/truckmate/emails/settings');
export const saveInboxSettings = (body) => tmJson('PUT', '/truckmate/emails/settings', body);
// ---- customer status emails (B / R loads) ----
export const getStatusMailSettings = () => tmJson('GET', '/truckmate/status-mail/settings');
export const saveStatusMailSettings = (body) => tmJson('PUT', '/truckmate/status-mail/settings', body);
export const getStatusMail = (trip) => tmJson('GET', `/truckmate/status-mail/${encodeURIComponent(trip)}`);
export const setStatusMailTo = (trip, { emails, phones }) => tmJson('PUT', `/truckmate/status-mail/${encodeURIComponent(trip)}`, { emails, phones });
// Breakdown switch: preview who gets told, then turn it on / off (email + text everyone on the load)
export const getBreakdownPreview = (trip) => tmJson('GET', `/truckmate/breakdown/${encodeURIComponent(trip)}/preview`);
// fix who gets the customer emails on a load: saved for the broker / bill-to for their next loads
export const setLoadContacts = (trip, emails) => tmJson('PUT', `/truckmate/contacts/${encodeURIComponent(trip)}`, { emails });
export const setBreakdown = (trip, body) => tmJson('POST', `/truckmate/breakdown/${encodeURIComponent(trip)}`, body);
export const sendStatusUpdate = (trip) => tmJson('POST', `/truckmate/status-mail/${encodeURIComponent(trip)}/send`, {});
// ---- Jarvis voice (Retell) ----
export const getVoiceStatus = () => tmJson('GET', '/voice/status');
export const setupVoice = () => tmJson('POST', '/voice/setup', {});
export const aiCall = (trip, { driver = 1, purpose = 'check' } = {}) => tmJson('POST', `/truckmate/trips/${encodeURIComponent(trip)}/ai-call`, { driver, purpose });
export const getJarvisCalls = () => tmJson('GET', '/voice/calls');
export const getDriverThread = (phone) => tmJson('GET', `/truckmate/drivers/${encodeURIComponent(String(phone).replace(/\D+/g, ''))}/thread`);
// Every truck and trailer with a live position (fleet map).
export const getFleetMap = () => tmJson('GET', '/truckmate/fleet-map');
// a truck's whole trip: GPS trail since dispatch, 15+ min stops (what happened), texts + Jarvis calls along the way
export const getJourney = (trip, fresh = false) => tmJson('GET', `/truckmate/journey/${encodeURIComponent(trip)}${fresh ? '?fresh=1' : ''}`);
// Whole-trip history of an OC load from the driver's phone: trail, stops, miles.
export const getOcHistory = (trip) => tmJson('GET', `/truckmate/oc/${encodeURIComponent(trip)}/history`);
